/**
 * Images on the wire (#307): an image never travels as base64 TEXT. The shrink/note/scrub rule lives in
 * ai.libx.js (and runs on every AIClient request); this module adds the on-disk image cache agentx needs.
 */

import { readFileSync, statSync } from 'node:fs';
import { imageWireOf, inlineImageForWire, scrubBase64Text, imageSize, imageNote, IMAGE_MAX_SIDE, IMAGE_MAX_BYTES, type ImageWire } from 'ai.libx.js';
import { forComponent } from './logging';

export { inlineImageForWire, scrubBase64Text, imageSize, imageNote, IMAGE_MAX_SIDE, IMAGE_MAX_BYTES };

const log = forComponent('imageWire');

type Wire = ImageWire & { key: string };
/** Small LRU: only the recent window's images are ever re-sent, so old base64 must not pin the heap. */
const cache = new Map<string, Wire>();
const CACHE_MAX = 8;
function remember(id: string, w: Wire): Wire {
  cache.delete(id);
  cache.set(id, w);
  if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value!);
  return w;
}

/**
 * The wire form of an image file: `{ url }` (data-URI, already shrunk if needed) or `{ note }`.
 * Null when the file is gone. Cached per path+mtime+size — this runs every send.
 */
export function imageForWire(path: string, mime: string, vision: boolean): { url: string } | { note: string } | null {
  let st;
  try { st = statSync(path); } catch (e) { log.warn(`image unreadable: ${path}`, e); return null; }
  const key = `${st.mtimeMs}:${st.size}`;
  let hit = cache.get(path);
  if (!hit || hit.key !== key) hit = remember(path, { key, ...imageWireOf(readFileSync(path), mime, path, path) });
  return vision && hit.url ? { url: hit.url } : { note: hit.note };
}

/** Model tokens for an image of w×h (Anthropic: w·h/750, ~1600 max after its ≤1568px resize; OpenAI/
 *  Gemini tiles land in the same range). Unknown dims → the cap. */
export const IMAGE_TOKENS_MAX = 1600;
export function imageTokens(dims: { w: number; h: number } | null): number {
  return dims ? Math.min(IMAGE_TOKENS_MAX, Math.max(1, Math.ceil((dims.w * dims.h) / 750))) : IMAGE_TOKENS_MAX;
}

/** Token-estimate weight of a wired image, in chars (×4 = the estimator's chars/token): an image costs
 *  its pixel tokens, NOT its base64 length (a 1536² PNG was 718k chars ≈ 180k "tokens" vs ~1.6k real).
 *  A note weighs its text. */
export function wiredImageWeight(r: { url: string } | { note: string }): number {
  if ('note' in r) return r.note.length;
  const b64 = r.url.slice(r.url.indexOf(',') + 1);
  return 4 * imageTokens(imageSize(Buffer.from(b64.slice(0, 4096), 'base64')));
}

/** Token-estimate weight of `imageForWire` (see `wiredImageWeight`). */
export function imageWireWeight(path: string, mime: string, vision: boolean): number {
  const r = imageForWire(path, mime, vision);
  return r ? wiredImageWeight(r) : 0;
}
